/**
 * Utilitaires pour la manipulation et l'analyse des tests
 */

/**
 * Compte le nombre de tests dans testsJson de manière robuste
 * Gère les différents formats: array, string JSON, double-stringified, etc.
 * @param {*} testsJson - Données de tests (peut être array, string, etc.)
 * @returns {number} - Nombre de tests
 */
export function getTestsCount(testsJson) {
  if (!testsJson) return 0;
  
  try {
    // Si c'est déjà un array
    if (Array.isArray(testsJson)) {
      return testsJson.length;
    }
    
    // Si c'est une string, essayer de parser
    if (typeof testsJson === 'string') {
      let parsed = JSON.parse(testsJson);
      
      // Double parsing si nécessaire (strings imbriqués)
      while (typeof parsed === 'string' && parsed.length > 0) {
        parsed = JSON.parse(parsed);
      }
      
      return Array.isArray(parsed) ? parsed.length : 0;
    }
    
    // Cas particulier: objet avec une propriété length
    if (testsJson.length !== undefined) {
      // Vérifier que ce n'est pas une string déguisée
      return typeof testsJson.length === 'number' ? testsJson.length : 0;
    }
    
    return 0;
  } catch (e) {
    console.warn('Erreur parsing testsJson:', e);
    return 0;
  }
}

/**
 * Agrège tous les tests des nœuds en un format adapté pour le flux visuel
 * @param {Array} nodes - Liste des nœuds contenant des tests
 * @returns {Object} - Nœuds et arêtes pour le flux de visualisation
 */
export function aggregateAllTestsToFlow(nodes) {
  let allNodes = [];
  let allEdges = [];
  let globalIdx = 0;

  nodes.forEach((node) => {
    const testsCount = getTestsCount(node.testsJson);
    if (testsCount > 0) {
      node.testsJson.forEach((test, tIdx) => {
        const lines = (test.description || test.title || "")
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        let prevId = null;
        lines.forEach((line, idx) => {
          const id = `n${globalIdx}_${tIdx}_${idx}`;
          // Coloration selon Given/When/Then
          let type = "default";
          if (/^Given/i.test(line)) type = "input";
          else if (/^When/i.test(line)) type = "process";
          else if (/^Then/i.test(line)) type = "output";
          allNodes.push({
            id,
            data: { label: `${node.name}: ${line}` },
            position: { x: 100 + tIdx * 300, y: globalIdx * 80 + idx * 60 },
            draggable: true,
            style: {
              background:
                type === "input"
                  ? "#fbbf24"
                  : type === "process"
                    ? "#60a5fa"
                    : type === "output"
                      ? "#34d399"
                      : "#f3f4f6",
              color: "#111827",
              border: "2px solid #d1d5db",
              borderRadius: 8,
              fontWeight: 500,
              fontSize: 14,
              padding: 8,
              transition: "box-shadow 0.2s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              cursor: "pointer",
            },
          });
          if (prevId) {
            allEdges.push({
              id: `e_${prevId}_${id}`,
              source: prevId,
              target: id,
              animated: true,
            });
          }
          prevId = id;
        });
        globalIdx++;
      });
    }
  });

  return { nodes: allNodes, edges: allEdges };
}

/**
 * Agrège les scénarios de test en flux visuel
 * @param {Array} testScenarios - Liste des scénarios de test
 * @returns {Object} - Nœuds et arêtes pour le flux de visualisation
 */
export function aggregateAllTestScenariosToFlow(testScenarios) {
  let allNodes = [];
  let allEdges = [];
  let globalIdx = 0;

  const horizontalSpacing = 260;
  const verticalSpacing = 160;

  testScenarios.forEach((test, tIdx) => {
    const allPaths = extractStepsByType(test); // array of step arrays
    allPaths.forEach((lines, pathIdx) => {
      // Add type as badge in first node if relevant
      if (
        (test.type === "automatisé" ||
          test.type === "exploratoire" ||
          test.type === "acceptance") &&
        lines.length > 0
      ) {
        lines[0] = `[${test.type.charAt(0).toUpperCase() + test.type.slice(1)
          }] ${lines[0]}`;
      }
      let prevId = null;
      const colors = ["#a5b4fc", "#fbbf24", "#60a5fa", "#34d399", "#f472b6"];
      const color = colors[(tIdx + pathIdx) % colors.length];
      lines.forEach((line, idx) => {
        const id = `n${globalIdx}_${tIdx}_${pathIdx}_${idx}`;
        allNodes.push({
          id,
          data: { label: line },
          position: {
            x: (tIdx + pathIdx) * horizontalSpacing,
            y: idx * verticalSpacing,
          },
          draggable: true,
          style: {
            background: color,
            color: "#111827",
            border: "2px solid #6366f1",
            borderRadius: 8,
            fontWeight: 500,
            fontSize: 14,
            padding: 8,
            transition: "box-shadow 0.2s",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            cursor: "pointer",
          },
        });
        if (prevId) {
          allEdges.push({
            id: `e_${prevId}_${id}`,
            source: prevId,
            target: id,
            animated: true,
          });
        }
        prevId = id;
      });
      globalIdx++;
    });
  });

  return { nodes: allNodes, edges: allEdges };
}

/**
 * Analyse les tableaux markdown et les convertit en composant React
 * @param {Array} lines - Lignes de texte à analyser
 * @param {Number} startIdx - Index de départ
 * @returns {Object} - Table JSX et index suivant
 */
export function parseMarkdownTable(lines, startIdx) {
  const tableRows = [];
  let idx = startIdx;
  while (idx < lines.length && /^\s*\|.*\|\s*$/.test(lines[idx])) {
    tableRows.push(lines[idx].trim());
    idx++;
  }
  // Remove separator line (|---|---|) if present
  if (tableRows.length > 1 && /^(\|\s*-+\s*)+\|$/.test(tableRows[1])) {
    tableRows.splice(1, 1);
  }
  // Transform to <table>
  const headers = tableRows[0]
    .split("|")
    .slice(1, -1)
    .map((h) => h.trim());
  const rows = tableRows.slice(1).map((row) =>
    row
      .split("|")
      .slice(1, -1)
      .map((cell) => cell.trim())
  );
  return {
    table: (
      <table
        className="markdown-table"
        style={{ margin: "16px 0", borderCollapse: "collapse", width: "100%" }}
      >
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  border: "1px solid #ccc",
                  padding: "4px",
                  background: "#f3f4f6",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i}>
              {cells.map((cell, j) => (
                <td
                  key={j}
                  style={{ border: "1px solid #ccc", padding: "4px" }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    ),
    nextIdx: idx,
  };
}

/**
 * Extrait les scénarios Gherkin d'un texte
 * @param {String} text - Texte à analyser
 * @returns {Array} - Scénarios Gherkin extraits
 */
export function extractAllGherkinScenarios(text) {
  // Match tous les blocs ```gherkin ... ```
  const regex = /```(?:gherkin)?\n([\s\S]*?)```/gi;
  let match;

  const scenarios = [];
  while ((match = regex.exec(text)) !== null) {
    scenarios.push(match[1].trim());
  }
  // Si aucun bloc markdown, tente de splitter par "Feature:" ou "Scenario:"
  if (scenarios.length === 0) {
    const fallback = text.match(
      /(?:Feature:|Scenario:)[\s\S]*?(?=(?:Feature:|Scenario:|$))/gi
    );
    if (fallback) {
      fallback.forEach((s) => scenarios.push(s.trim()));
    }
  }
  return scenarios;
}

/**
 * Extrait les scénarios de test structurés d'un texte
 * @param {String} text - Texte à analyser
 * @returns {Array} - Scénarios de test structurés
 */
export function extractAllTestScenarios(text) {
  const scenarios = [];

  // 0. PRIORITY: Extract JSON blocks (AI format)
  const jsonBlockRegex = /```(?:json)?\s*(\[[\s\S]*?\])\s*```/gi;
  let jsonMatch;
  while ((jsonMatch = jsonBlockRegex.exec(text)) !== null) {
    try {
      const jsonContent = jsonMatch[1];
      const parsedScenarios = JSON.parse(jsonContent);
      if (Array.isArray(parsedScenarios)) {
        parsedScenarios.forEach(scenario => {
          // Normaliser la structure
          const normalizedScenario = {
            type: "Test", // Force type to "Test"
            title: scenario.title || scenario.name || `Scénario ${scenarios.length + 1}`,
            description: scenario.description || "",
            steps: scenario.steps || [],
            positive: scenario.positive !== false, // default true unless explicitly false
            category: scenario.category || "functional",
            priority: scenario.priority || "medium",
            code: scenario.code || "",
            id: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`,
            uniqueKey: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`
          };
          scenarios.push(normalizedScenario);
        });
      }
    } catch (error) {
      console.warn("Erreur lors du parsing JSON d'un scénario:", error);
    }
  }

  // If JSON scenarios found, return them as priority
  if (scenarios.length > 0) {
    console.log(`✅ ${scenarios.length} scénarios extraits du JSON`);
    return scenarios;
  }

  // 1. Extract structured Gherkin acceptance criteria
  const gherkinScenarioRegex =
    /\*\*Scénario \d+\s*:\s*([^*]+)\*\*\s*\n\s*-\s*\*\*Given\*\*\s*:\s*([^\n]+)\s*\n\s*-\s*\*\*When\*\*\s*:\s*([^\n]+)\s*\n\s*-\s*\*\*Then\*\*\s*:\s*([^\n]+)/gi;

  let match;
  while ((match = gherkinScenarioRegex.exec(text)) !== null) {
    const [_, title, given, when, then] = match;
    scenarios.push({
      type: "Test",
      title: title.trim(),
      description: `Given ${given.trim()}\nWhen ${when.trim()}\nThen ${then.trim()}`,
      steps: [
        { type: "Given", text: given.trim() },
        { type: "When", text: when.trim() },
        { type: "Then", text: then.trim() },
      ],
      id: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`,
      uniqueKey: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`
    });
  }

  // 2. Extraire les tableaux Markdown
  const mdTableRegex = /\| *Given *\| *When *\| *Then *\|([\s\S]*?)(?=\n\n|\n###|$)/gi;

  while ((match = mdTableRegex.exec(text)) !== null) {
    const tableContent = match[1];
    const rows = tableContent
      .split("\n")
      .map((row) => row.trim())
      .filter((row) => row.startsWith("|") && !row.match(/^\|\s*-+\s*\|/));
    for (const row of rows) {
      const cells = row
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean);
      if (cells.length >= 3) {
        scenarios.push({
          type: "Test",
          title: `Test ${scenarios.length + 1}`, 
          description: `Given ${cells[0]}\nWhen ${cells[1]}\nThen ${cells[2]}`,
          steps: [
            { type: "Given", text: cells[0] },
            { type: "When", text: cells[1] },
            { type: "Then", text: cells[2] },
          ],
          id: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`,
          uniqueKey: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`
        });
      }
    }
  }

  // 3. Extract automated scenarios with their code
  const automatedTestRegex =
    /\d+\.\s*\*\*([^*]+)\*\*\s*\n\s*-\s*\*\*Cypress Test\*\*\s*:\s*\n\s*```(?:javascript|js)?\s*([\s\S]*?)```/gi;
  while ((match = automatedTestRegex.exec(text)) !== null) {
    const [_, title, code] = match;
    scenarios.push({
      type: "Test",
      title: title.trim(),
      description: title.trim(),
      code: code.trim(),
      id: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`,
      uniqueKey: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`
    });
  }

  // 4. Extract exploratory scenarios
  const exploratoryTestRegex = /\d+\.\s*\*\*([^*]+)\*\*\s*\n\s*-\s*([^\n]+)/gi;
  while ((match = exploratoryTestRegex.exec(text)) !== null) {
    const [_, title, description] = match;

    // Check if this is not an automated test (already extracted)
    if (!description.includes("**Cypress Test**")) {
      scenarios.push({
        type: "Test",
        title: title.trim(),
        description: description.trim(),
        additionalDescription: description.trim(),
        id: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`,
        uniqueKey: `test_${Date.now()}_${scenarios.length}_${Math.random().toString(36).substr(2, 9)}`
      });
    }
  }

  // 5. Nettoyage : retirer les doublons
  return scenarios.filter(
    (s, i, arr) =>
      arr.findIndex(
        (x) =>
          (x.description === s.description && x.type === s.type) ||
          (x.code === s.code && s.code)
      ) === i
  );
}

/**
 * Extrait les étapes Gherkin d'une description
 * @param {String} description - Description du test
 * @returns {Array} - Étapes Gherkin
 */
export function extractGherkinSteps(description) {
  const lines = description.split("\n").map((l) => l.trim());
  const steps = [];
  let title = "";
  for (let l of lines) {
    if (/^Scenario:/i.test(l)) {
      title = l.replace(/^Scenario:\s*/i, "");
    }
    const match = l.match(/^(Given|When|Then|And|But)\b\s*(.*)/i);
    if (match) {
      steps.push({
        type: match[1],
        text: match[2],
      });
    }
  }
  // Fallback: if no steps, look for markdown Given/When/Then table
  if (steps.length === 0) {
    const tableRegex = /\|.*\|/g;
    const tableLines = description.match(tableRegex);
    if (tableLines && tableLines.length > 1) {
      const headers = tableLines[0]
        .split("|")
        .map((h) => h.trim())
        .filter(Boolean);
      const scenarios = [];
      for (let i = 1; i < tableLines.length; i++) {
        if (/^-+$/.test(tableLines[i].replace(/\|/g, "").replace(/\s/g, "")))
          continue;
        const values = tableLines[i]
          .split("|")
          .map((v) => v.trim())
          .filter(Boolean);
        // One path/scenario = all steps from the line
        const scenarioSteps = headers
          .map((h, idx) => (values[idx] ? { type: h, text: values[idx] } : null))
          .filter(Boolean);
        if (scenarioSteps.length > 0) scenarios.push(scenarioSteps);
      }
      // If scenarios found, return first scenario (most probable)
      if (scenarios.length > 0) return scenarios[0];
    } else {
      const firstLine = lines.find(
        (l) => l && !/^[-*]/.test(l) && !/^#+/.test(l)
      );
      if (firstLine) steps.push({ type: "Step", text: firstLine });
    }
  }
  // By default, return structured steps array
  return steps;
}

/**
 * Extrait les étapes d'acceptation d'une description
 * @param {String} description - Description du test
 * @returns {Array} - Étapes d'acceptation
 */
export function extractAcceptanceSteps(description) {
  // Tableau markdown
  if (/\|.*\|/.test(description)) {
    const lines = description.split("\n").map((l) => l.trim());
    const headerIdx = lines.findIndex((l) => /^\|.*\|$/.test(l));
    if (headerIdx !== -1 && lines.length > headerIdx + 2) {
      const headers = lines[headerIdx]
        .split("|")
        .map((h) => h.trim())
        .filter(Boolean);
      const values = lines[headerIdx + 2]
        .split("|")
        .map((v) => v.trim())
        .filter(Boolean);
      // Regroupe en une phrase lisible
      return [
        headers
          .map((h, i) => `${h}: ${values[i] || ""}`)
          .join(" | ")
          .replace(/\s*\|\s*$/, ""),
      ];
    }
  }
  // Sinon, split par puce ou phrase
  return description
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^\|.*\|$/.test(l) && !/^#+/.test(l));
}

/**
 * Extrait les étapes de test selon leur type
 * @param {Object} test - Test à analyser
 * @returns {Array} - Étapes de test
 */
export function extractStepsByType(test) {
  if (Array.isArray(test.steps) && test.steps.length > 0) {
    return [test.steps];
  }
  if (test.type === "gherkin") {
    const gherkin = extractGherkinSteps(test.description);
    if (Array.isArray(gherkin[0])) return gherkin;
    return [gherkin];
  }
  if (test.type === "acceptance") {
    // Addition: split on Gherkin keywords for each line
    return [
      (test.description || "")
        .split(/(?=Given |When |Then |And |But )/g)
        .map((l) => l.trim())
        .filter(Boolean),
    ];
  }
  if (test.type === "automatisé" || test.type === "exploratoire") {
    const lines = test.description
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    let steps = [];
    if (lines.length > 0 && !/^[-•]/.test(lines[0])) {
      steps.push(lines[0]);
      lines.shift();
    }
    steps = [
      ...steps,
      ...lines
        .map((l) =>
          l.startsWith("-") || l.startsWith("•") ? l.slice(1).trim() : l
        )
        .filter((l) => l && l.length < 120),
    ];
    return [steps];
  }
  // Fallback
  return [
    [
      ...(test.description || test.title || "")
        .split(/(?=Given |When |Then |And |But )/g)
        .map((l) => l.trim())
        .filter(Boolean),
    ],
  ];
}